import type { DiscoveryJobStatus, DiscoveryResultCard, DiscoverySearchMode } from '@discovr/types';
import { LOOKALIKE_SEEDS_MAX, PROMPT_MIN_LENGTH } from './findLimits';

export const SEARCH_TIMING = { firstResults: '5-10 min', whole: '15-30 min' } as const;

export const RUNNING_HINT = `Runs in the background - usually ${SEARCH_TIMING.whole}, longer when it needs another round to find enough good fits.`;
export const LIVE_SKELETON_NOTE = `Creators start showing up once their reels have been watched - usually ${SEARCH_TIMING.firstResults} into a ${SEARCH_TIMING.whole} run.`;
export const RUN_STATUS_UNAVAILABLE = 'Couldn’t load how this search is doing.';

export const MODE_PLACEHOLDER: Readonly<Record<DiscoverySearchMode, string>> = {
  modash: 'Type keywords - wash-day routine, frizz, oiling…',
  prompt: 'Describe the creators you want, or leave it empty to search for the persona…',
  lookalike: 'Type an @handle - paste a list, or add one by one',
};
export const ANOTHER_SEED_PLACEHOLDER = 'Add another…';
export const TEXT_FIELD_LABEL = { prompt: 'Describe the creators', modash: 'Keywords' } as const;

export const COMPOSER_HINT = {
  noPersonas: 'Set up a persona on the Persona tab first.',
  noPersonaChosen: 'Add a persona with + to start.',
  seeds: `Up to ${LOOKALIKE_SEEDS_MAX} handles - every lookalike is checked against the persona.`,
  text: 'Every creator is checked against the persona.',
} as const;

export const START_HINT = {
  running: 'Stop it above to start another.',
  noPersonas: 'Set up a persona first - searches can’t start without one.',
  noPersonaChosen: 'Add a persona to start.',
  reading: 'Reading the persona’s search filters first…',
  noSeeds: 'Add at least one handle.',
  shortPrompt: `Write at least ${PROMPT_MIN_LENGTH} characters, or clear it to search for the persona.`,
  noKeywords: 'Type a few keywords - what these creators post about.',
} as const;

export const START_LABEL = { idle: 'Start search', running: 'Search running…' } as const;

export const SEED_NOTICE = {
  notHandle: 'That isn’t an Instagram handle.',
  tooMany: (max: number) => `Up to ${max} handles.`,
  duplicate: 'Already added.',
} as const;

export const COST_COPY = {
  estimating: 'Estimating cost…',
  unavailable: 'Cost estimate unavailable',
  exact: (usd: string) => `Est. ${usd}`,
  range: (low: string, high: string) => `Est. ${low}-${high}`,
} as const;

export const FOOTER_NO_PERSONA = 'No persona yet';
export const footerSummary = (checks: number, questions: number) =>
  `${checks} vetting · ${questions} persona questions`;

export const POSTING_LABEL = {
  daily: 'Daily',
  every: (days: number) => `Every ${days} days`,
} as const;

export const FIND_TOAST = {
  started: 'Search started - you can leave this page',
  lookalike: (handle: string) =>
    `Lookalike set up from @${handle} - add more handles or start the search`,
  seedAdded: (handle: string) => `@${handle} added to the lookalike`,
  alreadySeed: (handle: string) => `@${handle} is already in the lookalike`,
  seedsFull: (max: number) => `A lookalike takes up to ${max} handles - remove one to add another`,
  addedToPool: (name: string) => `${name} added to the Pool`,
  removedFromShortlist: (name: string) => `${name} removed from shortlist`,
  rejected: (name: string) => `${name} rejected`,
  movedToPassed: (name: string) => `${name} moved to Passed`,
  bulkAddedToPool: (creators: string) => `${creators} added to the Pool`,
  bulkMovedToPassed: (creators: string) => `${creators} moved to Passed`,
  bulkRejected: (creators: string) => `${creators} rejected`,
  partlySaved: (saved: number, total: number, reason: string) =>
    `${saved} of ${total} saved - ${reason}`,
  notInSearch: (handle: string) => `@${handle} isn’t in this search`,
  alreadyFinished: 'This search had already finished',
  stopping: 'Stopping the search - checked creators are kept',
  stopped: 'Search stopped - checked creators kept',
} as const;

export const SEARCH_ALREADY_RUNNING = 'A search is already running for this campaign';

export const STOP_DIALOG = {
  title: 'Stop this search?',
  body: 'Creators already checked stay in the results. Everyone else is dropped, and the search can’t be resumed.',
  cancel: 'Keep running',
  confirm: 'Stop search',
} as const;

export const RUN_STATUS_LABEL: Readonly<Record<DiscoveryJobStatus, string>> = {
  queued: 'Queued',
  running: 'Running',
  done: 'Done',
  partial: 'Stopped early',
  cancelled: 'Stopped',
  stalled: 'Stalled',
  error: 'Failed',
};

export const RUN_COPY = {
  done: (found: number, passed: number) => `${found} found, ${passed} passed`,
  shortOfTarget: (found: number, passed: number, requested: number) =>
    `${found} found, ${passed} of ${requested} passed · not enough good matches to reach ${requested}`,
  untitled: 'Search',
  thisSearch: 'this search',
  unlisted: 'This search',
  by: (starter: string) => `by ${starter}`,
  waiting: 'Waiting to start',
  findingMore: 'Finding more creators',
  round: (number: number, of: number) => `Round ${number} of up to ${of}`,
  wanted: (short: number) => `${short} more good ${short === 1 ? 'fit' : 'fits'} wanted`,
  started: (starter: string | null, ago: string) =>
    starter ? `Started by ${starter} ${ago}` : `Started ${ago}`,
  canLeave: 'You can leave, it keeps going',
  failed: 'This search failed before it finished.',
} as const;

export const STARTER = { you: 'you', another: 'another admin' } as const;

export const VERDICT_LEVEL_LABEL: Readonly<
  Record<DiscoveryResultCard['verdict']['level'], string>
> = {
  'high-priority': 'High priority',
  review: 'Worth a look',
  skip: 'Weak fit',
};

export const SHEET_COPY = {
  noScoreParts: 'This run didn’t keep the score’s parts.',
  noPersonaQuestions: 'No persona questions were asked of this creator.',
  noReelCounts: 'Instagram didn’t share counts for these reels.',
  noReelCount: 'No count shared for this reel',
  safetyNotMeasured: 'Reels couldn’t be watched, so safety wasn’t measured.',
  notInView: 'Not in this view',
  rank: (index: number, total: number) => `#${index + 1} of ${total}`,
  personaMatch: 'Matches your persona',
  noPersonaMatch: 'No persona match',
  checksPassed: (passed: number, asked: number) => `${passed} of ${asked} checks passed`,
  safetyFlag: 'safety flag',
  onShortlist: 'On your shortlist',
  worthShortlisting: 'Worth shortlisting?',
  weight: (percent: number) => `${percent}% weight`,
  vettingPassed: (passed: number, total: number) => `${passed}/${total} passed`,
  requirement: { must: 'Must', nice: 'Nice' },
  safetyFlagged: 'Something to note',
  safetyClear: 'Nothing unsafe found',
  checksFailed: (checks: string) => `${checks} failed`,
  allChecksPassed: 'Every check passed',
  unmeasured: (count: number) => `${count} couldn’t be measured from the posts read.`,
  allMeasured: 'Nothing left unmeasured.',
} as const;

export const PLACE_ACTION = { reject: 'Reject', restore: 'Move to Passed' } as const;

export const SHORTLIST_COPY = {
  label: 'Shortlist',
  remove: 'Shortlisted - remove',
  removeTitle: 'Shortlisted · click to remove',
} as const;

export const FIT_LABEL = (persona: string | null) => (persona ? `Fit · ${persona}` : 'Fit');
export const DISCARDED_BY_YOU = 'Discarded by you.';

export const EMPTY_TAB = {
  filtered: { title: 'No creators match', body: 'Try another name, or show all personas.' },
  failed: {
    title: 'This search failed',
    body: 'Nothing was checked to completion. Start the search again from the panel.',
  },
  nonePassed: {
    title: 'No one passed this search',
    body: 'Usually a must-have question is too strict, or the filters are too narrow. Check Rejected to see what failed most, then loosen that question on the persona.',
  },
  review: {
    title: 'Nothing to review',
    body: 'Creators land here when a must-have couldn’t be measured, for example when their reels couldn’t be watched.',
  },
  rejected: {
    title: 'No one rejected',
    body: 'Creators who fail a preset or must-have question show here, with the reason.',
  },
  checking: { title: 'Still checking', body: 'Creators appear here as they’re checked.' },
  statusUnknown: {
    title: 'No one has passed yet',
    body: 'This search’s status didn’t load, so it may still be checking creators.',
  },
} as const;

export const ARRIVAL_SORT_LABEL = 'Sort: Arrival order';

export const POOL_COPY = {
  subtitle:
    'Every creator your searches found for this campaign. Shortlist the ones you want to reach out to.',
  shortlisted: (creators: string) => `${creators} shortlisted`,
  removed: (creators: string) => `${creators} removed from shortlist`,
  found: 'Found',
  outreach: {
    noAccess: 'You can’t add creators to this campaign’s outreach.',
    instagramOnly: 'Only Instagram creators with a handle can go to outreach.',
    title: (creators: string) => `Send ${creators} to outreach?`,
    body: (adding: string, leftOut: readonly string[]) =>
      leftOut.length
        ? `${adding} will be added to this campaign’s outreach. Left out: ${leftOut.join(', ')}.`
        : `${adding} will be added to this campaign’s outreach.`,
    alreadyIn: (count: number) => `${count} already in outreach`,
    cantAdd: (count: number) => `${count} that can’t be added`,
    nothingNew: 'Every selected creator is already in outreach or can’t be added.',
    confirm: 'Send to outreach',
    cancel: 'Cancel',
    sent: (creators: string) => `${creators} sent to outreach`,
    open: 'Open outreach',
  },
  empty: {
    title: 'No creators yet',
    body: 'Run a search and shortlist the creators you like - they’ll collect here.',
  },
  noMatch: { title: 'No creators match', body: 'Try another name or handle.' },
  noShortlist: {
    title: 'Nothing shortlisted yet',
    body: 'Open a search and use Make shortlist on creators you want to reach out to.',
  },
  allEmpty: { title: 'No creators yet', body: 'Run a search and shortlist the creators you like.' },
} as const;

/** The Outreach screen's Pull creators confirm: it says "Add", like the button that opened it; the Pool page keeps POOL_COPY.outreach. */
export const OUTREACH_PULL_COPY = {
  title: (creators: string) => `Add ${creators} to outreach?`,
  confirm: 'Add to outreach',
} as const;

export const PULL_DIALOG_COPY = {
  title: 'Pull creators from Discovery',
  subtitle:
    'Ranked against this campaign’s personas. Creators arrive with their stats. They land in outreach as Not contacted; no mail is sent.',
  columns: { creator: 'Creator', email: 'Email', followers: 'Followers', avgViews: 'Avg views', er: 'ER', fit: 'Fit' },
  emailMissing: 'Not found yet',
  lookupFailed: 'Could not check',
  retry: 'Retry',
  taken: 'Already in outreach',
  selected: (count: number) => `${count} selected`,
  noEmail: (count: number) => `${count} ${count === 1 ? 'has' : 'have'} no email yet. The agent will look for one.`,
  cancel: 'Cancel',
  add: (count: number) => (count ? `Add ${count} to outreach` : 'Add to outreach'),
  empty: { shortlisted: 'No Instagram creators are shortlisted yet. Try All.', all: 'No Instagram creators in this Pool yet.', filtered: 'No creators match this filter.' },
  loadFailed: 'Could not load this campaign’s Pool.',
} as const;

export const FILTER_COPY = {
  set: (count: number) => `${count} set`,
  more: (first: string, more: number) => `${first} +${more}`,
  content: 'Niche & content',
  niche: 'Niche',
  partlyInRange: 'Partly inside the range',
  seedCap: (typical: number, max: number) =>
    `One handle usually finds about ${typical} creators, never more than ${max}. Add more handles to find more.`,
} as const;
